import { ColumnState } from "ag-grid-community";
import { DateRange } from "react-day-picker";
import { create } from "zustand";

import {
	defaultTQC3010_R01HeaderDto,
	TQC3010_R01_COMP_CD,
	TQC3010_R01DetailDto,
	TQC3010_R01HeaderDto,
} from "./Dto_TQC3010_R01";

export interface TQC3010_R01Filter {
	compCd: string;
	dateRange: DateRange | undefined;
	customCd: string;
	customNm: string;
	itemCd: string;
	itemNm: string;
}

export const defaultTQC3010_R01Filter: TQC3010_R01Filter = {
	compCd: TQC3010_R01_COMP_CD,
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: new Date(),
	},
	customCd: "",
	customNm: "",
	itemCd: "",
	itemNm: "",
};

export interface TQC3010_R01Info {
	inspectorEmp: string;
	inspectorEmpNm: string;
	approvorEmpno: string;
	approvorEmpNm: string;
}

export const defaultTQC3010_R01Info: TQC3010_R01Info = {
	inspectorEmp: "",
	inspectorEmpNm: "",
	approvorEmpno: "",
	approvorEmpNm: "",
};

interface Store_TQC3010_R01 {
	TQC3010_R01Filter: TQC3010_R01Filter;
	TQC3010_R01PastFilter: TQC3010_R01Filter;
	setTQC3010_R01Filter: (filter: Partial<TQC3010_R01Filter>) => void;
	setTQC3010_R01PastFilter: (filter: Partial<TQC3010_R01Filter>) => void;
	TQC3010_R01Info: TQC3010_R01Info;
	setTQC3010_R01Info: (info: Partial<TQC3010_R01Info>) => void;
	TQC3010_R01List: TQC3010_R01HeaderDto[];
	setTQC3010_R01List: (list: TQC3010_R01HeaderDto[]) => void;
	selectedTQC3010_R01: TQC3010_R01HeaderDto | undefined;
	setSelectedTQC3010_R01: (item: TQC3010_R01HeaderDto | undefined) => void;
	initSelectedTQC3010_R01: () => void;
	TQC3010_R01DetailList: TQC3010_R01DetailDto[];
	setTQC3010_R01DetailList: (list: TQC3010_R01DetailDto[]) => void;
	updateTQC3010_R01DetailList: (valueIndex: number, data: TQC3010_R01DetailDto) => void;
	originalTQC3010_R01DetailList: TQC3010_R01DetailDto[];
	setOriginalTQC3010_R01DetailList: (list: TQC3010_R01DetailDto[]) => void;
	allCount: number;
	setAllCount: (allCount: number) => void;
	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;
	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (state: ColumnState[] | null) => void;
	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;
	initData: () => void;
}

const withHeaderIndex = (list: TQC3010_R01HeaderDto[]) =>
	list.map((item, index) => ({
		...defaultTQC3010_R01HeaderDto,
		...item,
		valueIndex: index + 1,
	}));

const withDetailIndex = (list: TQC3010_R01DetailDto[]) =>
	list.map((item, index) => ({ ...item, valueIndex: index + 1, isModified: false }));

export const useTQC3010_R01Store = create<Store_TQC3010_R01>((set) => ({
	TQC3010_R01Filter: defaultTQC3010_R01Filter,
	TQC3010_R01PastFilter: defaultTQC3010_R01Filter,
	setTQC3010_R01Filter: (filter) =>
		set((state) => ({ TQC3010_R01Filter: { ...state.TQC3010_R01Filter, ...filter } })),
	setTQC3010_R01PastFilter: (filter) =>
		set((state) => ({
			TQC3010_R01PastFilter: { ...state.TQC3010_R01PastFilter, ...filter },
		})),
	TQC3010_R01Info: defaultTQC3010_R01Info,
	setTQC3010_R01Info: (info) =>
		set((state) => ({ TQC3010_R01Info: { ...state.TQC3010_R01Info, ...info } })),
	TQC3010_R01List: [],
	setTQC3010_R01List: (list) => set({ TQC3010_R01List: withHeaderIndex(list) }),
	selectedTQC3010_R01: undefined,
	setSelectedTQC3010_R01: (item) => set({ selectedTQC3010_R01: item }),
	initSelectedTQC3010_R01: () => set({ selectedTQC3010_R01: undefined }),
	TQC3010_R01DetailList: [],
	setTQC3010_R01DetailList: (list) =>
		set({ TQC3010_R01DetailList: withDetailIndex(list) }),
	updateTQC3010_R01DetailList: (valueIndex, data) =>
		set((state) => ({
			TQC3010_R01DetailList: state.TQC3010_R01DetailList.map((item) =>
				item.valueIndex === valueIndex ? data : item,
			),
		})),
	originalTQC3010_R01DetailList: [],
	setOriginalTQC3010_R01DetailList: (list) =>
		set({ originalTQC3010_R01DetailList: withDetailIndex(list) }),
	allCount: 0,
	setAllCount: (allCount) => set({ allCount }),
	columnState: null,
	setColumnState: (columnState) => set({ columnState }),
	detailColumnState: null,
	setDetailColumnState: (detailColumnState) => set({ detailColumnState }),
	scrollTop: null,
	setScrollTop: (scrollTop) => set({ scrollTop }),
	detailScrollTop: null,
	setDetailScrollTop: (detailScrollTop) => set({ detailScrollTop }),
	initData: () =>
		set({
			TQC3010_R01Filter: defaultTQC3010_R01Filter,
			TQC3010_R01PastFilter: defaultTQC3010_R01Filter,
			TQC3010_R01Info: defaultTQC3010_R01Info,
			TQC3010_R01List: [],
			selectedTQC3010_R01: undefined,
			TQC3010_R01DetailList: [],
			originalTQC3010_R01DetailList: [],
			allCount: 0,
			scrollTop: null,
			detailScrollTop: null,
		}),
}));

export default useTQC3010_R01Store;
